import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { archiveEntryReason, isArchiveMetadata, renderArchiveTree } from '../../archive/archive-tree';
import { groupMsp360Archive } from '../../archive/msp360-archive';
import { openRemoteZip } from '../../archive/remote-zip';
import { resolveExtension, validateExtensionMap } from '../../formats/extension-rules';
import type {
  ArchiveEntryInfo,
  ArchiveFormat,
  ArchiveLoadedEvent,
  ArchiveSelection,
  DocumentErrorEvent,
  DocumentProgressEvent,
  ExtensionMap,
  SizeUnits,
  UnknownFileBehavior
} from '../../models/viewer-types';
import { formatFileSize } from '../../utils/file-size';

@Component({
  selector: 'mbs-archive-toc',
  standalone: true,
  encapsulation: ViewEncapsulation.ShadowDom,
  templateUrl: './archive-toc.component.html',
  styleUrls: ['../../styles/theme.scss', './archive-toc.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArchiveTocComponent implements OnDestroy {
  readonly unknownFileBehavior = input<UnknownFileBehavior>('reject');
  readonly sizeUnits = input<SizeUnits>('binary');
  readonly format = input<ArchiveFormat>('zip');
  readonly url = input<string | null>(null);
  readonly fileName = input('');
  readonly extensionMap = input<ExtensionMap>({});
  readonly maxArchiveBytes = input(1024 * 1024 * 1024);
  readonly maxFileBytes = input(30 * 1024 * 1024);
  readonly maxPdfBytes = input(100 * 1024 * 1024);
  readonly timeoutMs = input(60000);
  readonly loaded = output<ArchiveLoadedEvent>();
  readonly entrySelected = output<ArchiveSelection>();
  readonly overviewRequested = output<void>();
  readonly progressChange = output<DocumentProgressEvent>();
  readonly loadError = output<DocumentErrorEvent>();

  private readonly tree = viewChild<ElementRef<HTMLElement>>('tree');
  readonly name = signal('');
  readonly size = signal<number | null>(null);
  formattedSize(): string {
    return formatFileSize(this.size() ?? 0, this.sizeUnits());
  }
  readonly info = signal('');
  readonly error = signal('');
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private controller?: AbortController;
  private version = 0;
  private ready = false;
  private refreshPolicy?: () => void;
  private treeController?: ReturnType<typeof renderArchiveTree>;

  /** The archive to read, and the limits it is read under. How sizes read is not among them. */
  private readonly openRequest = computed(() => ({
    url: this.url(),
    fileName: this.fileName(),
    format: this.format(),
    extensionMap: this.extensionMap(),
    maxArchiveBytes: this.maxArchiveBytes(),
    maxFileBytes: this.maxFileBytes(),
    maxPdfBytes: this.maxPdfBytes(),
    timeoutMs: this.timeoutMs()
  }));

  constructor() {
    // How sizes read and what unknown files do are told to the tree that is already drawn; everything
    // else reads the archive again.
    effect(() => {
      const units = this.sizeUnits();

      this.unknownFileBehavior();

      untracked(() => {
        if (!this.ready) return;

        this.treeController?.setSizeUnits(units);
        this.refreshPolicy?.();
      });
    });
    effect(() => {
      const tree = this.tree();
      const { url, fileName } = this.openRequest();

      untracked(() => {
        this.ready = Boolean(tree) && isPlatformBrowser(this.platformId);

        if (!this.ready) return;

        if (url) void this.openUrl(url, fileName);
        else this.clear();
      });
    });
  }
  clear(): void {
    this.version++;
    this.controller?.abort();
    this.controller = undefined;
    this.treeController = undefined;
    this.refreshPolicy = undefined;
    this.tree()?.nativeElement.replaceChildren();
    this.name.set('');
    this.size.set(null);
    this.info.set('');
    this.error.set('');
  }
  selectEntry(id: number | null): void {
    this.treeController?.select(id === null ? null : { id });
  }
  overview(): void {
    this.treeController?.select(null);
    this.overviewRequested.emit();
  }
  async openUrl(value: string, fileName = '', format: ArchiveFormat = this.format()): Promise<void> {
    if (!this.ready) return;
    this.clear();
    const version = this.version;
    const controller = new AbortController();

    this.controller = controller;
    let catalogLoading = true;
    try {
      validateExtensionMap(this.extensionMap());
      const url = new URL(value, (this.tree() as ElementRef<HTMLElement>).nativeElement.ownerDocument.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) throw Error('Please use an HTTP or HTTPS URL.');
      if (location.protocol === 'https:' && url.protocol === 'http:') throw Error('An HTTPS page requires an HTTPS archive URL.');
      let name = fileName || 'archive.zip';
      if (!fileName) {
        try {
          name = decodeURIComponent(url.pathname.split('/').pop() || name);
        } catch {
          /* Keep the fallback name for malformed URL escapes. */
        }
      }
      this.name.set(name);
      this.info.set('Reading ZIP catalog…');
      const source = await this.zone.runOutsideAngular(() =>
        openRemoteZip(url.href, {
          signal: controller.signal,
          sizeUnits: this.sizeUnits(),
          timeoutMs: Number.isFinite(this.timeoutMs()) && this.timeoutMs() > 0 ? this.timeoutMs() : 60000,
          maxArchiveBytes:
            Number.isFinite(this.maxArchiveBytes()) && this.maxArchiveBytes() > 0 ? this.maxArchiveBytes() : 1024 * 1024 * 1024,
          maxFileBytes: Number.isFinite(this.maxFileBytes()) && this.maxFileBytes() > 0 ? this.maxFileBytes() : 30 * 1024 * 1024,
          maxPdfBytes: Number.isFinite(this.maxPdfBytes()) && this.maxPdfBytes() > 0 ? this.maxPdfBytes() : 100 * 1024 * 1024,
          formatForName: (name: string) => resolveExtension(name, this.extensionMap()).format,
          onProgress: (loaded: number, total?: number) => {
            if (this.version === version && catalogLoading)
              this.zone.run(() =>
                this.progressChange.emit({
                  phase: 'downloading',
                  percent: null,
                  loadedBytes: loaded,
                  totalBytes: total
                })
              );
          }
        })
      );
      if (controller.signal.aborted || this.version !== version) return;
      catalogLoading = false;
      this.size.set(source.size);
      const rawById = new Map(source.entries.map((entry: any) => [entry.id, entry]));
      const filtered = source.entries.filter((entry: any) => !isArchiveMetadata(entry.name));
      const visible = format === 'msp360-log-archive' ? groupMsp360Archive(filtered) : filtered;
      const entries: ArchiveEntryInfo[] = visible.map((entry: any) => ({
        id: entry.id,
        name: entry.name,
        ...(entry.displayName ? { displayName: entry.displayName } : {}),
        size: entry.size,
        compressedSize: entry.compressed,
        directory: entry.directory,
        disabledReason: entry.directory ? entry.reason : archiveEntryReason(entry, this.extensionMap(), this.unknownFileBehavior())
      }));
      const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
      this.treeController = renderArchiveTree(
        (this.tree() as ElementRef<HTMLElement>).nativeElement,
        visible,
        (entry: any) => {
          if (controller.signal.aborted || this.version !== version) return;
          const rawEntry = rawById.get(entry.id)!;
          const rule = entry.previewFormat
            ? { format: entry.previewFormat, language: '' }
            : resolveExtension(entry.name, this.extensionMap());
          if (['zip', 'msp360-log-archive'].includes(rule.format)) return;
          this.treeController?.select(entry);
          const selection: ArchiveSelection = {
            entry: entriesById.get(entry.id)!,
            source: {
              name: entry.displayName || entry.name,
              size: entry.size,
              format: rule.format,
              language: rule.language,
              load: async (signal) => new Uint8Array(await source.extract(rawEntry, signal)),
              ...(entry.method === 0
                ? {
                    readRange: (offset: number, length: number, signal: AbortSignal) => source.readRange(rawEntry, offset, length, signal)
                  }
                : {})
            }
          };
          this.zone.run(() => this.entrySelected.emit(selection));
        },
        this.extensionMap(),
        this.sizeUnits(),
        this.unknownFileBehavior()
      );
      this.refreshPolicy = () => {
        for (const entry of visible) {
          const info = entriesById.get(entry.id);
          if (info && !entry.directory) info.disabledReason = archiveEntryReason(entry, this.extensionMap(), this.unknownFileBehavior());
        }
        this.treeController?.setUnknownFileBehavior(this.unknownFileBehavior());
      };
      this.info.set('');
      this.zone.run(() => {
        this.loaded.emit({
          fileName: name,
          size: source.size,
          range: source.range,
          entries
        });
        this.progressChange.emit({ phase: 'ready', percent: 100 });
      });
    } catch (error) {
      if (controller.signal.aborted || this.version !== version) return;
      const message = error instanceof Error ? error.message : 'Unable to read ZIP archive.';
      this.error.set(message);
      this.info.set('Unable to read ZIP archive.');
      this.zone.run(() => this.loadError.emit({ code: 'load_failed', message }));
    }
  }
  ngOnDestroy(): void {
    this.ready = false;
    this.clear();
  }
}
